import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'

import { routeTree } from './routeTree.gen'
import { queryClient } from '@/lib/query-client'
import { NotFoundComponent } from '@/app/not-found'
import { RouteErrorComponent, RoutePendingComponent } from '@/app/route-error'
import { ThemeProvider } from '@/theme/theme-provider'
import { TooltipProvider } from '@/components/ui/tooltip'
import './styles/globals.css'

const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Data freshness is TanStack Query's job; the router should not cache it too.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    // The router's built-in fallbacks are a bare error dump and the words "Not
    // Found". Both are surfaces an operator will actually hit -- one after every
    // deploy, the other from any bookmark carried over from the Nuxt app.
    defaultErrorComponent: RouteErrorComponent,
    defaultNotFoundComponent: NotFoundComponent,
    // Routes are code-split, so a first visit to a page waits on a network fetch.
    // Under a third of a second the previous page simply stays, which reads as
    // instant; past that, silence reads as a dropped click.
    defaultPendingMs: 300,
    defaultPendingMinMs: 250,
    defaultPendingComponent: RoutePendingComponent
})

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root is missing from index.html')

createRoot(rootElement).render(
    <StrictMode>
        <ThemeProvider>
            <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                    <RouterProvider router={router} />
                </TooltipProvider>
            </QueryClientProvider>
        </ThemeProvider>
    </StrictMode>
)
